"use client"

import type { ReactNode } from "react"
import { useState } from "react"
import { CountUp } from "@/components/home/count-up"
import { WHITEPAPER_PDF_URL } from "@/lib/storage"

const EST_READ_MINUTES = 40

type WpSection = {
  id: string
  title: string
  sub: string
  body: ReactNode
  meta: { tag: string; size: string }
}

function WpTable({ headers, rows }: { headers: string[]; rows: (string | ReactNode)[][] }) {
  return (
    <div className="wp-table-wrap">
      <table className="wp-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const WP_SECTIONS: WpSection[] = [
  {
    id: "exec",
    title: "Executive Summary",
    sub: "// 01.EXECUTIVE",
    meta: { tag: "PRELUDE", size: "5.2 KB" },
    body: (
      <>
        <p>
          Rivyu is a <strong>Telegram Mini App</strong> on <strong>The Open Network (TON)</strong> that turns real engagement into{" "}
          <strong>RIVYU (RIV)</strong> token rewards. Users <strong>mine</strong> with an energy-limited session model, <strong>stake</strong> in multiple pools,{" "}
          <strong>claim</strong> daily and milestone rewards, complete <strong>social missions</strong>, grow through <strong>referral tiers</strong>,{" "}
          <strong>open chests</strong> for boosts and rare loot, <strong>fuse</strong> chests up the rarity ladder, trade RIV in the <strong>Black Market</strong>, play the <strong>weekly Lottery</strong>, band together in <strong>Clans</strong> that compete in weekly wars, and steer the product through <strong>Active-Score-weighted governance</strong>—with value moving on-chain through{" "}
          <strong>signed claims</strong> into dedicated reward pools.
        </p>
        <p>
          Supply is <strong>deflationary</strong>: every Black Market chest purchase, Chest Fusion fee, clan-creation fee, and governance vote permanently <strong>burns 50%</strong> of the RIV spent, with the other 50% recycled into the Utility Pool; the Lottery pot and clan rent route additional spend into the same ecosystem treasury. The vision remains: <strong>fair distribution</strong>,{" "}
          <strong>transparent rules</strong>, and <strong>users over pure speculation</strong>. This page reflects <strong>Whitepaper V1.4</strong> (July 2026), aligned with the live Rivyu Telegram Mini App and on-chain design.
        </p>
        <div className="wp-callout">
          <div className="ttl">// WHAT&apos;S NEW IN V1.4</div>
          Documents shipped systems since V1.2: the full <strong>Clan system</strong> (June–July 2026) — five factions, on-chain clan creation with a <strong>50% burn</strong>, non-withdrawable clan treasuries backed by a dedicated <strong>ClanTreasury</strong> contract, Clan Points, weekly bracketed <strong>Clan Wars</strong>, co-op missions, the provably-fair <strong>Ghost_Net Heist</strong>, and a mercenary economy. Also new: <strong>Active Score</strong> (0–1000 per-user reputation), the <strong>Voting DAO</strong> (Active-Score-weighted advisory governance with a RIV burn per vote), <strong>Chest Fusion</strong>, <strong>Partner Tasks</strong> and partner campaign links, <strong>guest onboarding</strong>, and the <strong>staged beta rollout</strong> model.
        </div>
      </>
    ),
  },
  {
    id: "tokenomics",
    title: "Tokenomics",
    sub: "// 02.SUPPLY",
    meta: { tag: "ECONOMICS", size: "9.4 KB" },
    body: (
      <>
        <p>
          <strong>Total supply: 1,000,000,000 RIVYU</strong> — fixed at mint; no additional minting after genesis.
        </p>
        <div className="wp-h3">Token distribution</div>
        <WpTable
          headers={["Allocation", "Tokens", "%"]}
          rows={[
            ["Mining pool", "300,000,000", "30%"],
            ["Staking rewards", "200,000,000", "20%"],
            ["Liquidity pool", "120,000,000", "12%"],
            ["Founder allocation", "120,000,000", "12%"],
            ["Strategic round", "50,000,000", "5%"],
            ["Growth & Partnerships†", "50,000,000", "5%"],
            ["Airdrops", "80,000,000", "8%"],
            ["Treasury", "50,000,000", "5%"],
            ["Public sale*", "30,000,000", "3%"],
            [<strong key="t">Total</strong>, <strong key="tt">1,000,000,000</strong>, <strong key="tp">100%</strong>],
          ]}
        />
        <p>
          The former <strong>10% Marketing</strong> allocation is now two equal <strong>5%</strong> tranches: <strong>Strategic round</strong> (partners &amp; marketing) and <strong>Growth &amp; Partnerships</strong>. †Growth &amp; Partnerships tokens are <strong>locked 12 months post-TGE</strong>.
        </p>
        <div className="wp-h3">Mining pool usage (300M)</div>
        <p>
          The mining-side pool funds <strong>mining claims</strong>, <strong>daily login</strong>, <strong>streak milestones</strong>, <strong>welcome bonuses</strong>, <strong>social-task payouts</strong>, <strong>partner-task payouts</strong>, and related engagement rewards. Off-chain logic enforces budgets; <strong>claims</strong> execute against the <strong>Mining Rewards Pool</strong> via <strong>backend-authorized signatures</strong>.
        </p>
        <div className="wp-h3">Deflationary burn (implemented)</div>
        <p>Genesis supply is fixed at <strong>1B RIV</strong>, but circulating supply <strong>decreases</strong> through on-chain sinks:</p>
        <WpTable
          headers={["Sink", "Burn rule"]}
          rows={[
            ["Black Market chest purchase", "50% burned, 50% → Utility Pool"],
            ["Chest Fusion fee", "50% burned, 50% → Utility Pool"],
            ["Clan creation fee", "50% burned, 50% → Utility Pool (pro-rata first-month rent held in clan treasury custody)"],
            ["Governance vote (100 RIV per vote)", "50% burned, 50% → Utility Pool"],
            ["Weekly Lottery pot", "40% → Utility Pool, 60% → winners"],
            ["Clan rent & disband flush", "100% → Utility Pool (recycling, no burn)"],
            ["Level-up fees & emergency-exit penalties", "Routed to Utility Pool"],
          ]}
        />
        <p>
          Burns are verified on-chain (TEP-74 burn legs correlated by query id) and surfaced in ecosystem metrics — including a live <strong>clan treasury flow</strong> aggregate in the app&apos;s Burn &amp; Utility panel — so <strong>total RIV burned</strong> is publicly auditable. The more the in-app economy is used, the tighter circulating supply becomes.
        </p>
      </>
    ),
  },
  {
    id: "architecture",
    title: "Product architecture",
    sub: "// 03.STACK",
    meta: { tag: "SYSTEM", size: "5.4 KB" },
    body: (
      <>
        <p>Rivyu is built in three layers:</p>
        <ul>
          <li>
            <strong>Client — Telegram Mini App</strong> — React, Vite, TypeScript, cyberpunk UI, TON Connect, Telegram SDK, i18n.
          </li>
          <li>
            <strong>Application backend — Convex</strong> — Real-time data and server functions for users, mining, energy, rewards, referrals, leaderboards, staking mirrors, clans, governance, admin, rate limits, ads, and time-boxed events.
          </li>
          <li>
            <strong>Settlement layer — TON</strong> — FunC smart contracts: jetton (RIV), staking, rewards pools, mining pool, founder vesting, utility pool, Chest Shop, Lottery Pool, Clan Treasury; <strong>Ed25519-signed claims</strong> link off-chain accrual to on-chain payouts.
          </li>
        </ul>
        <div className="wp-callout">
          <div className="ttl">// NOTE</div>
          Replaces early &quot;Supabase + Python&quot; descriptions with the <strong>Convex-first</strong> architecture in production code.
        </div>
      </>
    ),
  },
  {
    id: "mining",
    title: "Mining system (Proof of Engagement)",
    sub: "// 04.MINE",
    meta: { tag: "EARN", size: "6.8 KB" },
    body: (
      <>
        <p>
          Mining is <strong>session-based</strong>: the user starts a session; <strong>energy</strong> depletes at <strong>1 unit per second</strong> while mining; at zero energy, mining stops until regeneration or ad-based top-ups.
        </p>
        <div className="wp-h3">Base rate and formula</div>
        <ul>
          <li>
            <strong>Base rate:</strong> <strong>125 RIV per day</strong> (before halvings and level multipliers), implemented as a per-second rate in the backend.
          </li>
          <li>
            <strong>Level multiplier:</strong> Levels apply a mining bonus from <strong>0% (Level 1)</strong> up to <strong>+100% (Level 10)</strong> — up to <strong>2×</strong> effective rate versus Level 1.
          </li>
          <li>
            <strong>After halvings:</strong> base daily rate divided by <strong>2^phase</strong>, where <strong>phase</strong> is the current halving phase (<strong>0–5</strong>; see Halving).
          </li>
          <li>
            <strong>Clan-lane boosts:</strong> clan members can earn additional additive mining bonuses on top — Resonance Flame streaks (<strong>+2% / +4%</strong>), faction synergy (<strong>+1%</strong>), and timed Clan Surges (<strong>+6%</strong> for 30 minutes) — see Clan system.
          </li>
          <li>
            <strong>Minimum claim:</strong> <strong>100 RIV</strong> accumulated before a mining claim can be prepared for on-chain payout.
          </li>
        </ul>
        <p>
          Mining is <strong>not</strong> proof-of-work; it is <strong>proof of engagement</strong>: recurring opens, session time bounded by energy, and progression that rewards consistent participants.
        </p>
      </>
    ),
  },
  {
    id: "halving",
    title: "Halving and fair emission",
    sub: "// 05.HALVING",
    meta: { tag: "EMISSION", size: "5.6 KB" },
    body: (
      <>
        <p>
          Halvings tighten emission as the network grows. The live model is <strong>phased</strong>: <strong>five halving phases</strong>. The network advances <strong>one phase</strong> when <strong>either</strong> the user-count threshold <strong>or</strong> the mining-pool emission threshold for that phase is crossed — <strong>whichever comes first</strong> (OR trigger, not additive).
        </p>
        <div className="wp-h3">Phase thresholds</div>
        <WpTable
          headers={["Phase", "Users ≥", "…OR mining-pool RIV emitted ≥"]}
          rows={[
            ["1", "1,000", "10,000,000"],
            ["2", "2,500", "25,000,000"],
            ["3", "5,000", "50,000,000"],
            ["4", "7,500", "100,000,000"],
            ["5", "10,000", "150,000,000"],
          ]}
        />
        <div className="wp-h3">Effect on the rate</div>
        <p>
          <strong>Daily rate</strong> = <strong>125 / 2^phase</strong> (before level multipliers), where <code>phase</code> runs <strong>0 to 5</strong>. At phase 5 the base floors at <strong>≈3.906 RIV/day</strong>. Phases only advance — the base rate <strong>never increases</strong>.
        </p>
        <p>
          Clan economics are <strong>denominated in &quot;miner-days&quot;</strong> (multiples of the current base daily rate), so clan fees and rent <strong>automatically halve together with mining</strong> — the relative cost of running a clan stays constant across halvings.
        </p>
        <div className="wp-callout">
          <div className="ttl">// GOVERNANCE</div>
          Community poll (~79%) chose to <strong>keep mining and staking emission separate</strong> for halving: the emission threshold counts <strong>mining-pool emission only</strong> — staking rewards do not advance mining halving.
        </div>
      </>
    ),
  },
  {
    id: "energy",
    title: "Energy system",
    sub: "// 06.ENERGY",
    meta: { tag: "CAPACITY", size: "4.2 KB" },
    body: (
      <>
        <ul>
          <li>
            <strong>Consumption:</strong> 1 energy per second while mining (fixed across levels).
          </li>
          <li>
            <strong>Capacity and regeneration</strong> scale with level.
          </li>
        </ul>
        <WpTable
          headers={["Level", "Max energy", "Regen (per second)"]}
          rows={[
            ["1", "5,000", "1.0"],
            ["2", "6,347", "1.5"],
            ["…", "…", "…"],
            ["10", "100,000", "5.5"],
          ]}
        />
        <div className="wp-h3">Watch-and-earn (ads) — energy</div>
        <WpTable
          headers={["Tier", "Max views / day", "Energy per view"]}
          rows={[
            ["50_ads", "50", "100"],
            ["20_ads", "20", "500"],
            ["5_ads", "5", "1,000"],
          ]}
        />
        <div className="wp-h3">RIFT ad tiers (Data Rift)</div>
        <p>Separate tiers grant <strong>RIFT tokens</strong> for the Data Rift mini-game. Completing all views in a tier during the ad day grants a lump-sum RIFT payout:</p>
        <WpTable
          headers={["Tier", "Max views / day", "RIFT tokens"]}
          rows={[
            ["10_ads_rift", "10", "1"],
            ["25_ads_rift", "25", "5"],
          ]}
        />
        <p>Daily reset aligns with a defined <strong>UTC boundary</strong> (including an IST-based reset window for regional consistency).</p>
      </>
    ),
  },
  {
    id: "levels",
    title: "Levels and progression",
    sub: "// 07.LEVELS",
    meta: { tag: "XP", size: "4.2 KB" },
    body: (
      <>
        <p>
          Users advance from <strong>Level 1 to 10</strong> by spending <strong>RIV</strong>; payments route to the <strong>Utility Pool</strong> on-chain. Higher levels increase <strong>max energy</strong> and{" "}
          <strong>regeneration</strong>, not only mining speed.
        </p>
        <WpTable
          headers={["To level", "Cost (RIV)", "Mining bonus (additive)"]}
          rows={[
            ["2", "1,000", "+20%"],
            ["3", "5,000", "+30%"],
            ["4", "10,000", "+40%"],
            ["5", "50,000", "+50%"],
            ["6", "100,000", "+60%"],
            ["7", "250,000", "+70%"],
            ["8", "300,000", "+80%"],
            ["9", "400,000", "+90%"],
            ["10", "500,000", "+100% (2× vs L1)"],
          ]}
        />
        <p>
          Level 5 additionally unlocks <strong>badge passives</strong>, <strong>clan founding eligibility</strong>, and Ambassador-program eligibility.
        </p>
      </>
    ),
  },
  {
    id: "rewards",
    title: "Rewards system",
    sub: "// 08.REWARDS",
    meta: { tag: "CLAIMS", size: "8.1 KB" },
    body: (
      <>
        <div className="wp-h3">Daily login</div>
        <p>
          <strong>10 RIV</strong> per successful daily claim (calendar rules enforced server-side).
        </p>
        <div className="wp-h3">Streak milestones</div>
        <p>Consecutive daily claims unlock extra RIV on milestone days. Missing a day resets the streak.</p>
        <WpTable
          headers={["Streak day", "Extra bonus (RIV)"]}
          rows={[
            ["3", "+50"],
            ["7", "+150"],
            ["14", "+500"],
            ["30", "+1,000"],
          ]}
        />
        <div className="wp-h3">Welcome bonus</div>
        <p>
          <strong>5,000 RIV</strong> per eligible user, capped at <strong>100</strong> users total. Eligibility via an <strong>admin-managed whitelist</strong> (email-based onboarding).
        </p>
        <div className="wp-h3">Social tasks</div>
        <WpTable
          headers={["Task id", "Typical reward (RIV)", "Notes"]}
          rows={[
            ["Instagram follow", "500", "Scales down with halving count"],
            ["Telegram channel", "500", "Scales down with halving count"],
            ["First stake", "100", "Fixed; claim window closed (ended March 2026 UTC)"],
            ["Telegram discussion", "100", "Fixed reward"],
          ]}
        />
        <div className="wp-h3">Data Rift (implemented)</div>
        <p>
          Daily spin/coupon mini-game with weighted rewards: <strong>RIV</strong>, <strong>energy</strong>, <strong>chests</strong>, <strong>boosts</strong>, <strong>fragments</strong>, and rare cosmetics. Powered by free daily spins, energy, and <strong>RIFT tokens</strong> from ad tiers. RIV winnings accrue to a claimable balance and settle via the same <strong>signed-claim path</strong> as mining (100 RIV minimum).
        </p>
        <p>
          Deeper loops: <strong>fragment crafting</strong> into a DATA.RIFT chest, <strong>Temporal Cache</strong> delayed auto-open, weekly <strong>Fracture</strong> intensity (peaking Sunday), <strong>pity timer</strong> for rare outcomes, and <strong>purge</strong> to roll a fresh token-only board (spends RIFT).
        </p>
        <div className="wp-h3">Recurring engagement loops (implemented)</div>
        <ul>
          <li>
            <strong>Idea Lab</strong> — community proposals; approved ideas award chests. Outcomes also feed <strong>Active Score</strong>.
          </li>
          <li>
            <strong>Network Bounty (weekly)</strong> — rotating mission with chest payout.
          </li>
          <li>
            <strong>Syndicate Heist</strong> — nightly squad breach with chest rewards.
          </li>
          <li>
            <strong>Cipher Fragments</strong> — daily ritual chest.
          </li>
          <li>
            <strong>Free Daily Chest</strong> — once per UTC day.
          </li>
        </ul>
        <p>All chest awards use the same idempotent award path and provably-fair opening (see Chests).</p>
        <div className="wp-h3">Partner Tasks (implemented)</div>
        <p>
          Admin-managed <strong>cross-promotion tasks</strong> appear pinned in the missions area: complete a partner&apos;s action (e.g. try a partner Mini App) and claim a <strong>fixed RIV reward</strong> (negotiated per deal; <strong>not</strong> subject to halving scaling). Two validation modes: <strong>honor timer</strong> (claim after a short cooldown) or <strong>Partner API &quot;Verify &amp; Claim&quot;</strong> — the backend calls the partner&apos;s validation endpoint server-side and only credits on positive verification (fail-closed on errors). Payouts settle through the same signed-claim path as other rewards.
        </p>
      </>
    ),
  },
  {
    id: "referrals",
    title: "Referral system",
    sub: "// 09.REF",
    meta: { tag: "NETWORK", size: "5.2 KB" },
    body: (
      <>
        <div className="wp-h3">Ongoing commission</div>
        <p>
          Referrers earn a <strong>percentage</strong> of referred users&apos; qualifying earnings (mining, daily, staking rewards, etc.), implemented in Convex with claim flows to on-chain where applicable.
        </p>
        <div className="wp-h3">Referral tiers (implemented)</div>
        <WpTable
          headers={["Tier", "Name", "Min active referrals", "Commission", "Referrer bonus (RIV)"]}
          rows={[
            ["0", "SOLO RUNNER", "0", "10%", "500"],
            ["1", "NETRUNNER", "3", "12%", "750"],
            ["2", "FIXER", "10", "15%", "1,000"],
            ["3", "SHADOW BROKER", "25", "18%", "1,500"],
            ["4", "SYNDICATE BOSS", "50", "20%", "2,500"],
          ]}
        />
        <p>New referred users may receive a one-time welcome-style bonus (e.g. <strong>250 RIV</strong>) per configured rules.</p>
        <div className="wp-h3">Referral count milestones (implemented)</div>
        <WpTable
          headers={["Active referrals", "Reward"]}
          rows={[
            ["1", "1,000 energy"],
            ["3", "Data Shard chest"],
            ["5", "Encrypted Cache chest"],
            ["10", "Neural Vault chest"],
            ["20", "Quantum Core chest"],
            ["50", "Glitch Core chest (only deterministic mythic path)"],
          ]}
        />
        <div className="wp-h3">Partner campaign links (implemented)</div>
        <p>
          For growth partnerships, the referral system supports <strong>campaign links</strong>: a partner receives a branded <strong>campaign slug</strong>, and every signup through that link is attributed to the partner&apos;s account as the referring parent — flowing through the normal commission and milestone machinery. Partner integrations authenticate with <strong>scoped, hashed API keys</strong>, and a public <strong>partner validation endpoint</strong> lets partners verify Rivyu-side task completion for their own campaigns.
        </p>
      </>
    ),
  },
  {
    id: "ambassador",
    title: "Ambassador program",
    sub: "// 10.AMBASSADOR",
    meta: { tag: "LEADERSHIP", size: "5.8 KB" },
    body: (
      <>
        <p>
          Structured <strong>leadership layer above referrals</strong>: top referrers apply for higher commission, <strong>weekly network bonus</strong>, and <strong>deep commission</strong> on sub-ambassadors. Distinct from 1-to-1 recruiting and the planned Clan system.
        </p>
        <div className="wp-h3">Eligibility</div>
        <p>
          Applicants need <strong>level ≥ 5</strong>, <strong>account age ≥ 30 days</strong>, and <strong>Referral Tier 4 (Syndicate Boss)</strong>. Manual review; <strong>7-day trial</strong> requiring <strong>5 new active recruits</strong> to activate.
        </p>
        <div className="wp-h3">Ranks</div>
        <WpTable
          headers={["Rank", "Commission", "Weekly network bonus", "Weekly cap"]}
          rows={[
            ["Ghost Signal (entry)", "20%", "—", "—"],
            ["Neon Agent", "22%", "0.5% of network mining", "500 RIV"],
            ["Grid Warden", "24%", "1.5% of network mining", "1,000 RIV"],
            ["Neural Commander", "27%", "3% of network mining", "2,000 RIV"],
            ["Protocol Architect", "30%", "5% of network mining", "5,000 RIV"],
          ]}
        />
        <p>
          <strong>Protocol Architect capped at 5 ambassadors globally.</strong> Rank from a Network Health Score (active recruits, activation rate, sub-ambassadors). Specialisation tracks from Neon Agent: <em>Signal Hunter</em>, <em>Neural Broadcaster</em>, or <em>Grid Keeper</em> (switchable once per 60 days). Deep commission: extra <strong>2%</strong> on sub-ambassador sub-network activity.
        </p>
      </>
    ),
  },
  {
    id: "staking",
    title: "Staking system",
    sub: "// 11.STAKE",
    meta: { tag: "POOLS", size: "7.2 KB" },
    body: (
      <>
        <WpTable
          headers={["Pool id", "Product name", "Lock", "Reference APY*"]}
          rows={[
            ["flex", "Core Flex", "None", "5%"],
            ["30d", "Cryo Stasis", "30 days", "12%"],
            ["90d", "Deep Freeze", "90 days", "25%"],
            ["365d", "Absolute Zero", "365 days", "60%"],
          ]}
        />
        <p>
          *<strong>Reference APY</strong> is the target at a defined TVL per pool. <strong>Displayed APY</strong> moves with TVL under a <strong>fixed-emission</strong> model over <strong>720 days</strong> (24×30-day months), aligned with on-chain staking: each pool has an allocation emitted linearly; higher TVL → lower APY, with a <strong>display cap</strong> for bounded UI values.
        </p>
        <p>
          <strong>Minimum stake:</strong> <strong>1,000 RIV</strong> (app configuration).
        </p>
        <div className="wp-h3">Reward cadence</div>
        <ul>
          <li>
            <strong>Flexible pool:</strong> epoch aligned to <strong>daily</strong> accrual settlement.
          </li>
          <li>
            <strong>Locked pools:</strong> <strong>weekly</strong> epoch alignment, matching contract behavior.
          </li>
        </ul>
        <div className="wp-h3">Emergency exit</div>
        <p>
          Early exit from locked positions: <strong>10% penalty on accrued rewards</strong> (principal protected per contract rules); penalty flows to the <strong>Utility Pool</strong>.
        </p>
        <div className="wp-h3">Upgrades and migrations</div>
        <p>
          Current generation is <strong>staking V4</strong> (2026-05-24 audit-remediation deployment). <strong>V1–V3 are frozen</strong>; stakes carry a <code>contractVersion</code> tag so unstake/emergency-exit routes to the correct contract. New stakes are created on V4 only.
        </p>
      </>
    ),
  },
  {
    id: "chests",
    title: "Chests and the Black Market",
    sub: "// 12.LOOT",
    meta: { tag: "CHESTS", size: "7.6 KB" },
    body: (
      <>
        <p>
          Rivyu&apos;s loot economy: provably-fair chest opening plus a <strong>Black Market</strong> where RIV is spent — and partly <strong>burned</strong> — to buy chests.
        </p>
        <div className="wp-h3">Chest tiers</div>
        <WpTable
          headers={["Tier", "Theme", "Rarity", "Source"]}
          rows={[
            ["data_shard", "Data Shard", "Common", "Earn + Black Market"],
            ["encrypted_cache", "Encrypted Cache", "Rare", "Earn + Black Market + Fusion"],
            ["neural_vault", "Neural Vault", "Epic", "Earn + Black Market + Fusion"],
            ["quantum_core", "Quantum Core", "Legendary", "Earn-only (incl. Fusion)"],
            ["glitch_core", "Glitch Core", "Mythic", "Earn-only (incl. Fusion)"],
            ["data_rift", "DATA.RIFT", "Legendary", "Earn-only — Data Rift fragments"],
            ["signal_core", "Signal Core", "Ambassador", "Earn-only — Ambassador program"],
          ]}
        />
        <p>
          Awarded from referral milestones, streaks, ads, events, Idea Lab, Bug Bounty, Network Bounty, Syndicate Heist, Cipher Fragments, Data Rift crafting, clan level-ups and Ghost_Net Heists, and more. Only the first three tiers are purchasable; all higher tiers must be earned (or fused).
        </p>
        <div className="wp-h3">Provably-fair opening</div>
        <ol>
          <li>At award: server commits <code>sha256(serverSeed)</code> before open.</li>
          <li>On open: reveals <code>serverSeed</code> and <code>clientSeed</code>.</li>
          <li>
            <code>HMAC-SHA256(serverSeed, &quot;clientSeed:chestId&quot;)</code> selects reward from a published loot table.
          </li>
          <li>Seeds and outcome stored permanently for verification.</li>
        </ol>
        <div className="wp-h3">Black Market — 50% burn</div>
        <p>
          Purchases settle on-chain via <strong>Chest Shop</strong> with <strong>3-leg verification</strong> (transfer → burn → utility-pool forward): <strong>50% burned permanently</strong>, <strong>50% → Utility Pool</strong>. Chest credited only after confirmed split settlement.
        </p>
        <div className="wp-h3">Chest Fusion (implemented)</div>
        <p>
          <strong>Chest Fusion</strong> combines several <strong>unopened</strong> chests of one tier plus a RIV fee into <strong>one chest of the next tier</strong> — the only repeatable path to legendary and mythic chests:
        </p>
        <WpTable
          headers={["Fuse", "Inputs", "Base fee (RIV)", "Output"]}
          rows={[
            ["Data Shards →", "7", "300", "Encrypted Cache"],
            ["Encrypted Caches →", "5", "850", "Neural Vault"],
            ["Neural Vaults →", "3", "3,000", "Quantum Core"],
            ["Quantum Cores →", "3", "5,000", "Glitch Core"],
          ]}
        />
        <ul>
          <li>
            <strong>Fee settlement:</strong> paid on-chain through the same <strong>Chest Shop</strong> rails and 3-leg verification as purchases — <strong>50% burned, 50% → Utility Pool</strong>.
          </li>
          <li>
            <strong>Supercharge:</strong> base <strong>10% chance</strong> of a supercharged output (boosted ceremony loot table); a <strong>pity counter guarantees a supercharge after 8</strong> non-supercharged fusions.
          </li>
          <li>
            <strong>Reactor modules:</strong> trade fee against odds — <em>Standard</em>, <em>Stable</em> (+15% fee, +8 pp supercharge), <em>Efficient</em> (−15% fee, no supercharge chance), or <em>Breach Amp</em> (+10% fee, 1.5× chest-upgrade/breach weighting).
          </li>
          <li>
            <strong>Fusion resonance:</strong> an activity streak grants temporary perks (up to <strong>+5 pp supercharge</strong> and a <strong>15% fee discount</strong>).
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "lottery",
    title: "Weekly Lottery",
    sub: "// 13.LOTTERY",
    meta: { tag: "PRIZE", size: "3.4 KB" },
    body: (
      <>
        <p>Recurring <strong>weekly prize pool</strong>: buy tickets with RIV for a chance at a large payout.</p>
        <WpTable
          headers={["Ticket", "Price (RIV)"]}
          rows={[
            ["Bronze", "100"],
            ["Silver", "500"],
            ["Gold", "1,000"],
          ]}
        />
        <ul>
          <li>
            <strong>Cap:</strong> up to <strong>25 tickets per user, per pool, per week</strong> (anti-whale).
          </li>
          <li>
            <strong>Payout split:</strong> <strong>60% to 5 winners</strong> (30% / 12% / 9% / 4.5% / 4.5%); <strong>40% → Utility Pool</strong>.
          </li>
          <li>Pots, draws, and winners settled on-chain via the <strong>Lottery Pool</strong> contract.</li>
        </ul>
      </>
    ),
  },
  {
    id: "active-score",
    title: "Active Score",
    sub: "// 14.SCORE",
    meta: { tag: "REPUTATION", size: "4.6 KB" },
    body: (
      <>
        <p>
          <strong>Active Score</strong> is Rivyu&apos;s per-user reputation engine: a <strong>0–1000</strong> score recomputed <strong>daily</strong> for every full account (guests are excluded), derived from <strong>six native signals</strong> with per-axis caps so <strong>no single behavior can dominate</strong>:
        </p>
        <WpTable
          headers={["Signal", "Cap", "Basis"]}
          rows={[
            ["Mining", "250", "RIV mined in the trailing 30 days (flow, not lifetime)"],
            ["Staking", "250", "Amount staked"],
            ["Idea Lab", "150", "Approved (+50) and implemented (+100) proposals"],
            ["Bug Bounty", "150", "Confirmed/resolved reports, scaled by severity"],
            ["Ads", "100", "Watch-and-earn participation"],
            ["Account level", "100", "Level (1–10) × 10"],
          ]}
        />
        <p>
          The mining, staking, and ads axes combine an <strong>absolute on-ramp</strong> (predictable progress) with <strong>relative elite bands</strong> at the <strong>top 10% / 3% / 1%</strong> of the population, estimated from a daily distribution snapshot. Scores map to cosmetic tiers — <em>Drifter</em> (0), <em>Operator</em> (100), <em>Runner</em> (250), <em>Architect</em> (450), <em>Overlord</em> (700), <em>Legend</em> (900) — and the formula is <strong>versioned</strong>, so historical snapshots (e.g. for airdrops) remain auditable when weights are tuned.
        </p>
        <p>
          Active Score is the shared spine for <strong>governance vote weight</strong>, <strong>beta-access eligibility</strong>, leaderboard surfaces, and future airdrop criteria.
        </p>
      </>
    ),
  },
  {
    id: "governance",
    title: "Governance — the Voting DAO",
    sub: "// 15.DAO",
    meta: { tag: "GOVERN", size: "4.1 KB" },
    body: (
      <>
        <p>
          Rivyu runs <strong>advisory, Active-Score-weighted governance</strong> in-app: a real, costly signal on product direction that remains <strong>advisory</strong> — outcomes guide the roadmap but confer <strong>no ownership or control</strong> over the project.
        </p>
        <ul>
          <li>
            <strong>Proposals</strong> are created by the team across three categories — <strong>event</strong>, <strong>feature</strong>, <strong>integration</strong> — with <strong>2–8 options</strong> and a default <strong>72-hour</strong> voting window. Closed proposals stay visible with full tallies in a <strong>Past proposals</strong> archive.
          </li>
          <li>
            <strong>Vote allowance</strong> derives from Active Score: <code>votes = floor(score / 100)</code>, minimum <strong>1</strong> vote at score 100, hard-capped at <strong>10</strong> votes per proposal. Votes may be <strong>split across options</strong>.
          </li>
          <li>
            <strong>Every vote costs 100 RIV</strong>, settled on-chain through the Chest Shop rails with 3-leg verification — <strong>50% burned, 50% → Utility Pool</strong>. Sybil-resistant twice over: reputation-gated allowance plus a real burn cost per vote.
          </li>
          <li>
            <strong>Outcome</strong> is decided by <strong>plurality</strong>, gated by a configurable <strong>quorum</strong> (initially disabled). Below quorum → <em>No Decision</em>. Votes already cast are <strong>never clawed back</strong>.
          </li>
        </ul>
        <div className="wp-callout">
          <div className="ttl">// PRECEDENT</div>
          The <strong>halving-accounting decision</strong> (mining and staking emission kept separate, ~79% majority — see Halving) was run under this governance model.
        </div>
      </>
    ),
  },
  {
    id: "clans",
    title: "Clan system",
    sub: "// 16.CLANS",
    meta: { tag: "SYNDICATES", size: "9.8 KB" },
    body: (
      <>
        <p>
          Clans (in-app: <strong>syndicates</strong>) are Rivyu&apos;s competitive team layer, launched <strong>July 2026</strong>: player-run crews with a shared treasury, daily co-op streaks, weekly bracketed wars, and a faction meta-layer — about <strong>team competition</strong>, not recruiting commissions.
        </p>
        <div className="wp-h3">Factions and onboarding</div>
        <p>
          New operators go through a cinematic <strong>Glitch Night</strong> story onboarding, then swear an oath to one of <strong>five factions</strong> — <em>Netrunners</em>, <em>Edgerunners</em>, <em>Nomads</em>, <em>Corpo</em>, or <em>Independent</em>. <strong>Every faction is mechanically equal</strong> (identity, not advantage). <strong>Independents ride solo</strong>: they cannot found or join clans, but can be hired as <strong>mercenaries</strong>. Matching your operator faction to your clan&apos;s faction lights a passive <strong>+1% mining</strong> faction-synergy bonus.
        </p>
        <div className="wp-h3">Founding a clan and the miner-day economy</div>
        <p>
          Founding requires <strong>account level ≥ 5</strong>, <strong>account age ≥ 7 days</strong>, and a <strong>connected TON wallet</strong>. Clan costs are denominated in <strong>miner-days</strong> — multiples of the current base daily mining rate — so all clan pricing <strong>halves automatically with mining emission</strong>:
        </p>
        <ul>
          <li>
            <strong>Creation fee: 40 miner-days</strong> (5,000 RIV at the pre-halving base rate) plus pro-rata first-month rent, paid on-chain via the <strong>ClanTreasury</strong> contract with 3-leg verification: <strong>50% burned</strong>, <strong>50% → Utility Pool</strong>; a reconciliation cron recovers payments stranded by chain-indexing lag.
          </li>
          <li>
            <strong>Clan levels 1–10</strong> grow the member cap from <strong>8 → 52</strong> and mission slots from <strong>1 → 3</strong>; leveling costs pooled <strong>CP</strong> (500 → 50,000) plus treasury RIV (5 → 80 miner-days) and awards the leader a <strong>Glitch Core</strong> chest.
          </li>
          <li>
            <strong>Rent:</strong> monthly upkeep of <strong>3 → 30 miner-days</strong> by clan level, paid from the treasury (prepayable up to 3 months); rent routes <strong>100% to the Utility Pool</strong>. Falling behind: <em>rent overdue</em> → <strong>frozen</strong> after 7 days → <strong>auto-disband</strong> after 30 more.
          </li>
        </ul>
        <div className="wp-h3">Treasury — non-withdrawable by design</div>
        <p>
          Each clan has a pooled RIV <strong>treasury</strong> funded by on-chain member <strong>donations</strong> and an optional <strong>&quot;donate 10% of mining claims&quot;</strong> toggle. The treasury is <strong>intentionally non-withdrawable</strong> — no withdraw function exists, as an anti-exit-scam measure. Treasury RIV can only be <strong>consumed by clan operations</strong> (rent, missions, level-ups, mercenary cuts) or forfeited to the Utility Pool on disband; custody is reconciled on-chain via batched settlement transactions.
        </p>
        <div className="wp-h3">Clan Points (CP) and daily play</div>
        <p>
          <strong>CP</strong> is the clan-side activity currency: earned by member activity (capped at <strong>500 CP per member per day</strong>), <strong>non-transferable</strong>, <strong>never convertible to RIV</strong>. Clans spend pooled CP on <strong>Clan Surge</strong> (800 CP → <strong>+6% mining for the whole clan for 30 minutes</strong>, 6-hour cooldown), level-ups, and CP cosmetics (250–650 CP). The <strong>Resonance Flame</strong> daily co-op streak grants <strong>+2% / +4%</strong> clan mining boosts when <strong>≥ 50% / ≥ 80%</strong> of members are active; seven consecutive Gold days ignite <strong>Inferno</strong>. Every action feeds a permanent <strong>Chronicle</strong> archive and live feed.
        </p>
        <div className="wp-h3">Weekly Clan Wars</div>
        <ul>
          <li>Matched every <strong>Monday 00:00 UTC</strong> into <strong>brackets of 8</strong>; war score is CP driven by member mining.</li>
          <li>
            <strong>Sunday Showdown</strong> (23:00–23:59 UTC): war-score multiplier goes <strong>2×</strong> with a bracket kill-feed.
          </li>
          <li>
            <strong>Prizes:</strong> top three clans earn <strong>250 / 150 / 75 bonus CP</strong> plus a <strong>RIV prize pool</strong> (5,000 RIV baseline) paid to winners&apos; treasuries.
          </li>
          <li>
            <strong>Sponsor-a-War:</strong> Ambassadors can wire on-chain RIV into a bracket&apos;s prize pool, capped at <strong>10,000 RIV per war week</strong>.
          </li>
          <li>
            <strong>Rivals &amp; seasons:</strong> beating a pinned rival <strong>doubles the CP swing</strong>; wars roll up into <strong>4-week seasons</strong> — the <strong>top 10 clans each season</strong> enter a permanent <strong>Hall of Fame</strong>.
          </li>
        </ul>
        <div className="wp-h3">Co-op missions and the Ghost_Net Heist</div>
        <p>
          Officers start treasury-funded team objectives (e.g. <em>mine 1,000,000 RIV collectively</em>) with 7-day windows and CP rewards. The <strong>Ghost_Net Heist</strong> is a weekly, unannounced, <strong>provably-fair vault crack</strong>: enough of the clan must be actively mining during the window (60% / 45% / 30% participation gates by clan size); one participant is drawn — <code>winner = SHA-256(serverSeed + clientSeed) mod participants</code>, commit hash posted <strong>before</strong> close — and wins a chest up to a rare mythic roll.
        </p>
        <div className="wp-h3">Mercenaries, roles, and safety rails</div>
        <p>
          <strong>Independent</strong> operators can be hired as <strong>Ronin</strong>: clans post War or Heist contracts with an offered <strong>cut %</strong> of the settled reward. Roles are <strong>Leader / Co-Leader / Officer / Member</strong> with scoped permissions. Lifecycle guards: <strong>72-hour disband grace</strong>, claimable <strong>leaderless</strong> clans, <strong>24-hour cooldown</strong> after leaving, and a <strong>60-day cooldown</strong> before a disbanding leader can found again. Join modes are <strong>Open</strong>, <strong>Invite-only</strong>, or <strong>Application</strong>, with a public preview before joining.
        </p>
      </>
    ),
  },
  {
    id: "onchain",
    title: "On-chain contracts and treasury",
    sub: "// 17.CHAIN",
    meta: { tag: "TON", size: "5.8 KB" },
    body: (
      <>
        <WpTable
          headers={["Contract", "Role"]}
          rows={[
            ["Jetton minter", "1B RIV genesis; TEP-74 compliant"],
            ["Jetton wallet", "Per-holder balances, transfers, burns"],
            ["Staking", "Four pools, stakes, unstake, emergency exit"],
            ["Rewards pool", "200M staking reward budget; signed claims"],
            ["Mining rewards pool", "300M mining/engagement budget; signed claims"],
            ["Founder vesting", "120M locked; cliff after vesting period"],
            ["Utility pool", "Level-up RIV, penalties, chest/lottery/clan forwards, admin rules"],
            ["Chest Shop", "Black Market + Chest Fusion + governance-vote settlement: 50% burn, 50% → Utility Pool"],
            ["Lottery Pool", "Weekly pot: 60% winners, 40% → Utility Pool"],
            ["Clan Treasury", "Clan creation (50% burn / 50% Utility Pool), donation custody, batched rent settlement"],
          ]}
        />
        <p>
          <strong>Claims:</strong> Convex issues <strong>Ed25519</strong> signatures; contracts verify signer, amounts, and <strong>nonces</strong> against replay. Pools can be <strong>paused</strong> and signing keys <strong>rotated</strong> under admin controls. Money paths flowing <strong>into</strong> the ecosystem (chest purchases, fusion fees, votes, clan creation, donations) are verified <strong>leg-by-leg against on-chain data</strong> before anything is credited, with reconciliation crons recovering payments stranded by chain-indexing lag.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "Security, abuse prevention, identity",
    sub: "// 18.TRUST",
    meta: { tag: "HARDEN", size: "4.8 KB" },
    body: (
      <>
        <ul>
          <li>
            <strong>Rate limits</strong> on claims, mining toggles, and staking-related actions.
          </li>
          <li>
            <strong>CAPTCHA</strong> on the client for high-risk flows.
          </li>
          <li>
            <strong>Email OTP</strong> (e.g. Resend) plus <strong>wallet connection</strong> for recovery and sybil resistance.
          </li>
          <li>
            <strong>TON proof</strong> where configured, binding Telegram identity to wallet ownership.
          </li>
          <li>
            <strong>Admin panel:</strong> global toggles (mining/staking), whitelist, audit logging, contract registry — restricted to founder/admin identities.
          </li>
        </ul>
        <div className="wp-h3">Guest accounts (implemented)</div>
        <p>
          New users can <strong>start mining immediately as guests</strong> — no wallet or email required. Guest accounts are sandboxed: <strong>on-chain actions (claims, staking, purchases) are gated</strong> behind promoting to a full operator account, guests are <strong>excluded from the public user count</strong> and Active Score, and <strong>referral attribution is deferred</strong> until promotion so referral rewards only ever count real, promoted accounts.
        </p>
        <div className="wp-h3">Staged beta rollouts (implemented)</div>
        <p>
          Major new features (e.g. Chest Fusion, Clans) launch through a <strong>beta window</strong>: for roughly the first <strong>9 days</strong>, access requires an <strong>allowlist entry</strong> or an <strong>Active Score above 500</strong>; after the window the feature <strong>opens automatically to everyone</strong>. This throttles risk on new money paths while rewarding the most engaged operators with early access.
        </p>
        <div className="wp-h3">Bug Bounty program (implemented)</div>
        <WpTable
          headers={["Severity", "RIV reward", "Chest"]}
          rows={[
            ["Low", "100", "—"],
            ["Medium", "500", "—"],
            ["High", "2,500", "Encrypted Cache"],
            ["Critical", "10,000", "Quantum Core"],
          ]}
        />
        <p>Reports reviewed manually; RIV payouts settle through the signed-claim path once resolved and pool-eligible. Confirmed reports also feed <strong>Active Score</strong>.</p>
      </>
    ),
  },
  {
    id: "leaderboards",
    title: "Leaderboards and social proof",
    sub: "// 19.RANK",
    meta: { tag: "SOCIAL", size: "2.1 KB" },
    body: (
      <>
        <ul>
          <li>
            <strong>Global</strong> ranking by mined totals.
          </li>
          <li>
            <strong>Weekly</strong> leaderboard with scheduled reset.
          </li>
          <li>Scores aggregate major reward sources (mining, daily, welcome, social, referral) per backend rules.</li>
          <li>Clan wars and seasons add team-level rankings and a seasonal <strong>Hall of Fame</strong>.</li>
        </ul>
      </>
    ),
  },
  {
    id: "badges",
    title: "Operator badges",
    sub: "// 20.BADGES",
    meta: { tag: "COLLECT", size: "3.2 KB" },
    body: (
      <>
        <p>
          <strong>Collectible badges</strong> for milestones across mining, staking, referrals, chests, weekly Lottery, and community programs. Visible in <strong>About → My Badges</strong> and on profile surfaces.
        </p>
        <ul>
          <li>
            <strong>Equipped loadout:</strong> up to <strong>ten</strong> badges; only equipped badges grant passive boosts.
          </li>
          <li>
            <strong>Passive boosts:</strong> <strong>mining-rate %</strong> and <strong>energy-regen %</strong> — summed by stat, each clamped (currently <strong>20% / 20%</strong> cap).
          </li>
          <li>
            <strong>Level gate:</strong> passives <strong>inactive below level 5</strong> — badges remain visible but numeric boost is zero until level 5.
          </li>
        </ul>
        <p>Rarity tiers and per-badge values are being tuned; the badge system and vault UI are live today.</p>
      </>
    ),
  },
  {
    id: "events",
    title: "Seasonal events",
    sub: "// 21.EVENTS",
    meta: { tag: "LIVE", size: "2.8 KB" },
    body: (
      <>
        <p>
          The platform supports <strong>time-boxed, configuration-driven events</strong>. Example: <strong>April 2026 Easter</strong> — daily engagement for egg progression, referral milestones for higher-tier eggs, loot tables with weighted outcomes (RIV on-chain claims, energy shards, mining boosts, infinite energy windows, referral boosts, rare syndicate-style multipliers). Event windows and grace periods live in server config for reuse.
        </p>
      </>
    ),
  },
  {
    id: "tech",
    title: "Technology stack",
    sub: "// 22.BUILD",
    meta: { tag: "STACK", size: "2.4 KB" },
    body: (
      <>
        <WpTable
          headers={["Layer", "Technology"]}
          rows={[
            ["Blockchain", "TON"],
            ["Contracts", "FunC; Blueprint/tooling; tested deployment pipeline"],
            ["Client", "React 18, Vite, TypeScript, TON Connect UI"],
            ["Backend", "Convex (queries, mutations, actions, crons)"],
            ["Auth / comms", "Email OTP, Telegram WebApp APIs"],
            ["Localization", "i18next"],
          ]}
        />
      </>
    ),
  },
  {
    id: "roadmap",
    title: "Status and roadmap",
    sub: "// 23.SHIP",
    meta: { tag: "TIMELINE", size: "6.4 KB" },
    body: (
      <>
        <div className="wp-h3">Shipped today (implemented)</div>
        <p>
          Mining, energy, levels, daily/streak/welcome rewards, social tasks, referrals (commission tiers <strong>and</strong> count milestones), <strong>Ambassador program</strong>, staking on <strong>V4</strong> with frozen-legacy routing, signed claims, leaderboards, <strong>operator badges</strong>, ads (energy <strong>and RIFT</strong>), <strong>Data Rift</strong> (fragments, Temporal Cache, Fracture curve), admin tooling, antibot/rate limits, multi-language UI, contract integration — plus <strong>Chest &amp; Black Market</strong> with provably-fair opening and <strong>50% burn</strong>, <strong>weekly Lottery</strong>, recurring loops (<strong>Idea Lab</strong>, <strong>Network Bounty</strong>, <strong>Syndicate Heist</strong>, <strong>Cipher Fragments</strong>, <strong>Free Daily Chest</strong>), and <strong>Bug Bounty</strong>.
        </p>
        <p>
          Shipped in the June–July 2026 wave: <strong>Active Score</strong>, <strong>Chest Fusion</strong>, and the <strong>Voting DAO</strong> governance layer (all June 2026), the full <strong>Clan system</strong> (July 2026), plus <strong>guest onboarding</strong>, <strong>Partner Tasks</strong>, <strong>partner campaign links</strong>, and the <strong>staged beta rollout</strong> model.
        </p>
        <div className="wp-h3">Launching next (in design)</div>
        <ul>
          <li>
            <strong>Soulbound NFT membership passes — with perks</strong> — non-transferable TON NFTs minted on mainnet as a permanent badge of membership, with perks on an in-app timer: a one-time capped <strong>Founder Pass</strong> and a recurring tiered <strong>Citizen Pass</strong>. Perks reuse shipped systems — infinite energy, mining multipliers (stacked outside the 10× chest cap under a new global ceiling), bonus chests/Lottery gifts; <strong>TON and Telegram Stars</strong> rails.
          </li>
        </ul>
        <div className="wp-h3">On the horizon (planned)</div>
        <p>
          <strong>GameFi — Rivyu Arena:</strong> skill-based <strong>1v1 PvP</strong> with ELO and bracket rewards, plugging into the shipped Clan season cadence.
        </p>
        <div className="wp-h3">Path to public launch</div>
        <ul>
          <li>Independent security audit</li>
          <li>Mainnet deployment</li>
          <li>Liquidity and TGE execution per regulatory constraints</li>
          <li>Sustained monitoring and incident playbooks</li>
          <li>Optional contract upgrades (e.g. gas-forwarding) per engineering specs</li>
          <li>Clan treasury custody pre-mainnet gate: <strong>multisig control</strong> of the ClanTreasury admin key</li>
        </ul>
        <div className="wp-callout">
          <div className="ttl">// DATES</div>
          §23.2–23.3 are <strong>direction, not commitment</strong>. Legacy TGE/airdrop dates remain <strong>placeholders</strong> until confirmed by official channels.
        </div>
      </>
    ),
  },
  {
    id: "disclaimer",
    title: "Legal disclaimer and risk disclosure",
    sub: "// 24.LEGAL",
    meta: { tag: "NOTICE", size: "4.2 KB" },
    body: (
      <>
        <p>
          <strong>Please read carefully.</strong> By accessing Rivyu or interacting with RIVYU (RIV) or Rivyu smart contracts, you acknowledge the following.
        </p>
        <div className="wp-h3">Not advice</div>
        <p>
          This whitepaper describes <strong>intended and implemented behavior</strong> for <strong>informational purposes only</strong>. It is <strong>not</strong> investment, financial, trading, legal, accounting, or tax advice, nor a recommendation to buy, sell, or hold any token.
        </p>
        <div className="wp-h3">No offer or security</div>
        <p>
          This document is <strong>not</strong> an offer or solicitation of a security or regulated product. RIV is a <strong>utility token</strong> for in-app participation and <strong>confers no ownership, equity, dividend, or governance right</strong> in the project. The in-app Voting DAO is <strong>advisory only</strong>: proposal outcomes inform the roadmap and grant <strong>no control over, or interest in, the issuer</strong>.
        </p>
        <div className="wp-h3">Jurisdictional notice</div>
        <p>
          Treatment of digital assets varies by jurisdiction. Not directed where unlawful or unlicensed. <strong>India:</strong> not legal tender; tax/AML obligations may apply. <strong>United States:</strong> not registered under securities laws; not intended for unlawful use by U.S. persons. <strong>All regions:</strong> you are responsible for local compliance.
        </p>
        <div className="wp-h3">Risk disclosure</div>
        <p>
          Digital assets are <strong>highly volatile</strong>. <strong>Token prices can fall to zero.</strong> Smart contracts carry implementation, economic, and operational risk. Rewards, rates, halving parameters, clan economics, perks, and roadmap items are <strong>not guaranteed</strong>. Forward-looking statements describe <strong>direction, not commitment</strong>. <strong>Never commit funds you cannot afford to lose.</strong>
        </p>
        <div className="wp-h3">Your responsibility</div>
        <p>
          Verify contract addresses on <strong>official sources</strong>, read on-chain code, and consult qualified advisors. To the maximum extent permitted by law, the project and contributors <strong>accept no liability</strong> for loss arising from use of this whitepaper or Rivyu software.
        </p>
        <p style={{ fontSize: 12, color: "var(--text-faint)", fontStyle: "italic" }}>
          Rivyu Project — Whitepaper v1.4 — July 2026
        </p>
      </>
    ),
  },
]

export function WhitepaperView() {
  const [idx, setIdx] = useState(0)
  const cur = WP_SECTIONS[idx]!
  const total = WP_SECTIONS.length
  const progress = ((idx + 1) / total) * 100
  const minutesIn = Math.round(((idx + 1) / total) * EST_READ_MINUTES)
  const minutesLeft = Math.max(0, EST_READ_MINUTES - minutesIn)
  const pctEach = 100 / total

  return (
    <div className="wp-wrap">
      <a href="#whitepaper-main" className="wp-skip-to-doc">
        Skip to whitepaper content
      </a>
      <aside className="wp-toc">
        <div className="toc-head">
          <span>// INDEX / TOC</span>
          <span style={{ color: "var(--text-faint)" }}>v1.4</span>
        </div>
        <p className="wp-toc-lede-narrow" role="note">
          On phones and tablets, the article appears first. Use this list when you want to jump to another
          section.
        </p>
        {WP_SECTIONS.map((s, i) => {
          const klass = i === idx ? "current" : i < idx ? "complete" : ""
          const glyph = i === idx ? "◆" : i < idx ? "✓" : "○"
          return (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              className={`wp-toc-item ${klass}`}
              onClick={() => setIdx(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setIdx(i)
                }
              }}
            >
              <span className="num">{String(i + 1).padStart(2, "0")}.</span>
              <span>{s.title}</span>
              <span className="glyph">{glyph}</span>
            </div>
          )
        })}
        <div className="wp-toc-progress">
          <div className="lbl">
            <span>// PROGRESS</span>
            <span style={{ color: "var(--green-bright)" }}>{Math.round(progress)}%</span>
          </div>
          <div className="bar">
            <div className="fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="wp-toc-hint">◆ CLICK A SECTION TO JUMP_</div>
      </aside>

      <article className="wp-reader" id="whitepaper-main" key={idx}>
        <div className="wp-doc-head">
          <h1>
            Rivyu Whitepaper
            <span className="wp-doc-sub">The Community-Driven Web3 Ecosystem on TON · Version 1.4 · July 2026</span>
          </h1>
          <div className="version">
            VERSION 1.4
            <br />
            JULY 2026
            <br />
            <span className="ok">◆ ALIGNED W/ LIVE APP</span>
          </div>
        </div>
        <div className="wp-section-tag">
          <span className="num">
            [ {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} ]
          </span>
          <span className="sep">//</span>
          <span>{cur.sub}</span>
          <span className="sep">//</span>
          <span style={{ color: "var(--cyan)" }}>{cur.meta.tag}</span>
        </div>
        <h2 className="wp-h2">
          {cur.title}
          <span className="accent">_</span>
        </h2>
        {cur.body}

        <div className="wp-paginate">
          <button type="button" className="wp-pg-btn" disabled={idx === 0} onClick={() => setIdx(Math.max(0, idx - 1))}>
            ← PREV_SECTION
          </button>
          <a className="wp-pg-btn primary" href={WHITEPAPER_PDF_URL} download>
            ↓ DOWNLOAD_PDF
          </a>
          <button type="button" className="wp-pg-btn" disabled={idx === total - 1} onClick={() => setIdx(Math.min(total - 1, idx + 1))}>
            NEXT_SECTION →
          </button>
        </div>
      </article>

      <aside className="wp-meta">
        <div className="wp-meta-head">// DOC_META</div>
        <div className="wp-meta-row">
          <span className="k">DOC.ID</span>
          <span className="v">WP_V1.4</span>
        </div>
        <div className="wp-meta-row">
          <span className="k">NETWORK</span>
          <span className="v">TON (TESTNET-READY)</span>
        </div>
        <div className="wp-meta-row">
          <span className="k">STANDARD</span>
          <span className="v">TEP-74</span>
        </div>
        <div className="wp-meta-row">
          <span className="k">SUPPLY</span>
          <span className="v">1,000,000,000</span>
        </div>
        <div className="wp-meta-row">
          <span className="k">SECTION</span>
          <span className="v">
            {String(idx + 1).padStart(2, "0")}/{String(total).padStart(2, "0")}
          </span>
        </div>
        <div className="wp-meta-row">
          <span className="k">SIZE</span>
          <span className="v">{cur.meta.size}</span>
        </div>
        <div className="wp-meta-row">
          <span className="k">STATUS</span>
          <span className="v ok">DOC SYNC</span>
        </div>

        <div className="wp-section-prog">
          <div className="ttl">// READ_TRACE</div>
          <div className="dots">
            {WP_SECTIONS.map((_, i) => (
              <div key={i} className={`dot ${i < idx ? "done" : i === idx ? "current" : ""}`} />
            ))}
          </div>
        </div>

        <div className="wp-meta-stat">
          <div className="lbl">// MIN_READ</div>
          <div className="val">
            {minutesIn}
            <span style={{ fontSize: 14, color: "var(--text-faint)" }}> / {EST_READ_MINUTES} MIN</span>
          </div>
          <div className="delta">◆ EST. {minutesLeft}min remaining</div>
        </div>

        <div className="wp-meta-stat">
          <div className="lbl">// READ_PROGRESS</div>
          <div className="val">
            <CountUp to={progress} dur={1100} />
            <span style={{ fontSize: 14, color: "var(--text-faint)" }}>%</span>
          </div>
          <div className="delta">
            ◆ {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} SECTIONS · {pctEach.toFixed(1)}% EACH
          </div>
        </div>

        <div className="wp-meta-footnote">
          ◆ AUDIT PENDING FOR MAINNET
          <br />
          ◆ SOURCE: RIVYU-WHITEPAPER-V1.4
          <br />◆ LAST UPDATE: JULY 2026
        </div>
      </aside>
    </div>
  )
}
